import { useEffect, useRef } from 'react';
import { useAppStore } from '@/stores/useAppStore';
import { useGroupStore } from '@/stores/useGroupStore';
import { useAuthStore } from '@/stores/useAuthStore';
import { gamificationService } from '@/services/gamificationService';
import { supabase } from '@/integrations/supabase/client';

/**
 * Hook para sincronizar progresso de missões automaticamente
 * Monitora eventos do useAppStore e cria atividades correspondentes
 */
export const useMissionSync = () => {
  const { challenge, goals, habits } = useAppStore();
  const { myGroups } = useGroupStore();
  const { user } = useAuthStore();
  const processedHabits = useRef<Set<string>>(new Set());

  // Monitorar check-ins diários do desafio 90 dias
  useEffect(() => {
    if (!challenge || !challenge.completedDates || challenge.completedDates.length === 0) return;
    if (myGroups.length === 0) return;

    const lastCheckInDate = challenge.completedDates[challenge.completedDates.length - 1];
    const today = new Date().toISOString().split('T')[0];
    
    // Verificar se o último check-in foi hoje
    if (lastCheckInDate === today) {
      myGroups.forEach(async (group) => {
        try {
          await gamificationService.createActivityWithPoints({
            groupId: group.id,
            tipo: 'checkin',
            eventType: 'daily_checkin',
            payload: {
              date: lastCheckInDate,
              total_checkins: challenge.completedDates.length,
              streak: challenge.streak
            }
          });
        } catch (error) {
          console.error('Error syncing check-in with mission:', error);
        }
      });
    }
  }, [challenge, myGroups]);

  // Monitorar conclusão de metas
  useEffect(() => {
    if (!goals || goals.length === 0 || myGroups.length === 0) return;

    const completedGoals = goals.filter(g => g.status === 'concluido');
    
    completedGoals.forEach(async (goal) => {
      myGroups.forEach(async (group) => {
        try {
          await gamificationService.createActivityWithPoints({
            groupId: group.id,
            tipo: 'meta_concluida',
            eventType: 'goal_completed',
            payload: {
              goal_title: goal.title,
              goal_pillar: goal.pillar
            }
          });
        } catch (error) {
          console.error('Error syncing goal completion with mission:', error);
        }
      });
    });
  }, [goals, myGroups]);

  // Monitorar check-ins de hábitos vinculados a grupos
  useEffect(() => {
    if (!habits || habits.length === 0 || myGroups.length === 0 || !user) return;

    const today = new Date().toISOString().split('T')[0];

    habits.forEach(async (habit) => {
      // Verificar se hábito está vinculado a algum grupo
      const groupId = habit.dados_adicionais?.group_id;
      if (!groupId) return;

      // Criar chave única para este hábito + data
      const habitKey = `${habit.id}-${today}`;
      if (processedHabits.current.has(habitKey)) return;

      // Verificar se há check-in completo hoje
      const { data: todayCheckin } = await supabase
        .from('habit_checkins')
        .select('completed')
        .eq('habit_id', habit.id)
        .eq('date', today)
        .eq('completed', true)
        .maybeSingle();

      if (todayCheckin) {
        try {
          // Marcar como processado
          processedHabits.current.add(habitKey);

          // Criar atividade no grupo
          await gamificationService.createActivityWithPoints({
            groupId: groupId,
            tipo: 'habit_completed',
            eventType: 'habit_checkin',
            payload: {
              habit_title: habit.title,
              activity_name: habit.dados_adicionais?.activity_name,
              duration: habit.dados_adicionais?.min_duration,
              date: today
            }
          });

          // Buscar missão ativa do grupo
          const { data: activeMissions } = await supabase
            .from('group_missions')
            .select('id, progresso_atual, progresso_total')
            .eq('group_id', groupId)
            .eq('status', 'ativa');

          if (activeMissions && activeMissions.length > 0) {
            for (const mission of activeMissions) {
              // Atualizar progresso da missão
              await supabase
                .from('mission_progress')
                .upsert({
                  mission_id: mission.id,
                  user_id: user.id,
                  completed_days: 1,
                  contributed_today: true,
                  updated_at: new Date().toISOString()
                }, {
                  onConflict: 'mission_id,user_id'
                });
            }
          }
        } catch (error) {
          console.error('Error syncing habit with group:', error);
        }
      }
    });

    // Limpar cache de hábitos processados após 24h
    const cleanupInterval = setInterval(() => {
      processedHabits.current.clear();
    }, 24 * 60 * 60 * 1000);

    return () => clearInterval(cleanupInterval);
  }, [habits, myGroups, user]);
};
